/**
 * Name → component map for islands. Each island lazily imports its module
 * the first time `mount()` is called for that name, so the legacy host
 * page's import order (CDN supabase → `const sb = ...` → bridge to
 * `window.__sbClient`) completes before the React side ever instantiates
 * the supabase client.
 *
 * To add a new island: add its name to the union and a loader that returns
 * the component module's default export.
 */

import type { ComponentType } from 'react'

// intent-feed parked under _parked/intent-feed (PR B of pivot-to-core-crm.md).
// Reactivation: git mv _parked/intent-feed back here + re-add to the union + loaders.
export type IslandName =
  | 'accounts'
  | 'api-keys'
  | 'app-keys'
  | 'app-rail'
  | 'bookings'
  | 'business-settings'
  | 'businesses'
  | 'command-palette'
  | 'contacts'
  | 'gmail-compose'
  | 'pipeline'
  | 'setup-checklist'
  | 'today'
  | 'sites-settings'
  | 'sources'
  | 'outreach'
  | 'outreach-settings'
  | 'outreach-tools'
  | 'custom-fields-settings'
  | 'custom-fields-panel'

/**
 * Optional reply context passed to the gmail-compose island when the user
 * is replying to an existing thread (vs. composing fresh). Set by the
 * host page's contact-drawer code when the "Reply" affordance is clicked
 * on a synced gmail_messages row.
 */
export interface GmailComposeReplyContext {
  gmailThreadId: string
  gmailMessageId: string
  originalSubject: string
}

/**
 * Per-island compose context passed by the host when mounting
 * 'gmail-compose'. Other islands ignore this field. Kept loose
 * (single optional shape) rather than a discriminated union per
 * island name because we only have one island that needs custom
 * props today; a union refactor lands when a second island needs
 * its own shape.
 */
export interface GmailComposeContext {
  contactId: string
  contactEmail: string
  contactName?: string | null
  replyTo?: GmailComposeReplyContext | null
}

export interface IslandProps {
  user: { id: string; email?: string } | null
  composeContext?: GmailComposeContext
  /** 'custom-fields-panel': the record whose custom fields the panel edits. */
  customFieldsTarget?: { entity: 'contact' | 'account'; id: string }
}

const loaders: Record<IslandName, () => Promise<{ default: ComponentType<IslandProps> }>> = {
  accounts: () => import('./accounts/AccountsIsland'),
  'api-keys': () => import('./api-keys/ApiKeysIsland'),
  'app-keys': () => import('./app-keys/AppKeysIsland'),
  'app-rail': () => import('./app-rail/AppRail'),
  bookings: () => import('./bookings/BookingsIsland'),
  'business-settings': () => import('./businesses/BusinessSettingsIsland'),
  businesses: () => import('./businesses/BusinessesIsland'),
  'command-palette': () => import('./command-palette/CommandPalette'),
  contacts: () => import('./contacts/ContactsIsland'),
  'gmail-compose': () => import('./gmail-compose/GmailComposeIsland'),
  pipeline: () => import('./pipeline/PipelineIsland'),
  'setup-checklist': () => import('./setup-checklist/SetupChecklist'),
  'sites-settings': () => import('./businesses/SitesSettingsIsland'),
  sources: () => import('./sources/SourcesIsland'),
  today: () => import('./today/TodayFocus'),
  outreach: () => import('./outreach/OutreachDashboard'),
  'outreach-settings': () => import('./outreach/OutreachSettings'),
  'outreach-tools': () => import('./outreach/tools/OutreachToolsIsland'),
  'custom-fields-settings': () => import('./outreach/CustomFieldsSettings'),
  'custom-fields-panel': () => import('./outreach/CustomFieldsPanel'),
}

export async function loadIsland(name: IslandName): Promise<ComponentType<IslandProps>> {
  const loader = loaders[name]
  const mod = await loader()
  return mod.default
}
